import React, { useEffect, useState } from "react";

// Renders a string that may contain LaTeX math delimited by $...$ (inline) or $$...$$
// (display). Math segments are rendered with KaTeX (loaded via CDN in public/index.html,
// with the mhchem chemistry extension); plain segments render as ordinary text. Question
// text, options, passages and explanations across the app are authored/scraped as LaTeX
// source and displayed through this component.
//
// Only KaTeX-produced HTML is injected via dangerouslySetInnerHTML; user text outside the
// delimiters is rendered as React children (escaped), and KaTeX runs with
// throwOnError:false so malformed input degrades to visible source rather than breaking.

// Match $$…$$ (display) or $…$ (inline) math. The inline body allows an ESCAPED dollar (\$)
// so questions that use "$" as a literal symbol — e.g. "$x\$y$", "$9\$(6\$2)$" — pair their
// delimiters correctly instead of closing at the escaped dollar. KaTeX renders \$ as a literal $.
const MATH_RE = /\$\$([\s\S]+?)\$\$|\$((?:\\\$|[^$\n])+?)\$/g;

function katexReady() {
  return typeof window !== "undefined" && !!window.katex;
}

// KaTeX is loaded with `defer`, so on first paint window.katex may not exist yet. This hook
// flips to true as soon as it's available (short poll), triggering a re-render so math that
// initially showed as raw source becomes typeset.
function useKatexReady() {
  const [ready, setReady] = useState(katexReady());
  useEffect(() => {
    if (ready) return undefined;
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      if (katexReady()) {
        setReady(true);
        clearInterval(id);
      } else if (tries > 50) {
        clearInterval(id); // give up after ~5s; fall back to plain source
      }
    }, 100);
    return () => clearInterval(id);
  }, [ready]);
  return ready;
}

function renderMath(latex, displayMode) {
  try {
    return window.katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      strict: false,
      trust: false,
    });
  } catch {
    return null;
  }
}

/**
 * @param {{ text?: string, className?: string, style?: object, as?: string }} props
 */
export default function MathText({ text, className, style, as: Tag = "span" }) {
  const ready = useKatexReady();
  const value = text == null ? "" : String(text);

  // Preserve authored newlines (and normal wrapping) everywhere questions/options/explanations
  // are shown. Callers can still override via an explicit `whiteSpace` in `style`.
  const mergedStyle = { whiteSpace: "pre-wrap", ...(style || {}) };

  // Fast path: no math delimiters (or KaTeX not ready yet) → plain text, whitespace kept.
  if (!ready || value.indexOf("$") === -1) {
    return (
      <Tag className={className} style={mergedStyle}>
        {value}
      </Tag>
    );
  }

  const parts = [];
  let lastIndex = 0;
  let match;
  MATH_RE.lastIndex = 0;
  let key = 0;
  while ((match = MATH_RE.exec(value)) !== null) {
    if (match.index > lastIndex) {
      parts.push(value.slice(lastIndex, match.index));
    }
    const isDisplay = match[1] !== undefined;
    const latex = isDisplay ? match[1] : match[2];
    const html = renderMath(latex, isDisplay);
    if (html) {
      parts.push(
        <span
          key={`m${key++}`}
          // Reset to normal inside typeset math so the parent's pre-wrap doesn't perturb
          // KaTeX's own spacing.
          style={{ whiteSpace: "normal" }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    } else {
      // KaTeX failed — show the original source (including delimiters) verbatim.
      parts.push(match[0]);
    }
    lastIndex = MATH_RE.lastIndex;
  }
  if (lastIndex < value.length) {
    parts.push(value.slice(lastIndex));
  }

  return (
    <Tag className={className} style={mergedStyle}>
      {parts}
    </Tag>
  );
}
